import { useRef, useState, useCallback } from 'react'
import { UploadCloud, FileText, AlertCircle } from 'lucide-react'

interface FileUploadZoneProps {
  onUpload: (files: FileList) => Promise<void>
  uploadProgress?: Record<string, number>
  disabled?: boolean
}

export function FileUploadZone({ onUpload, disabled = false }: FileUploadZoneProps) {
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const pdfFiles = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf')
      if (pdfFiles.length > 0) {
        const dataTransfer = new DataTransfer()
        pdfFiles.forEach(f => dataTransfer.items.add(f))
        onUpload(dataTransfer.files)
      }
    }
  }, [onUpload])

  const handleClick = useCallback(() => {
    inputRef.current?.click()
  }, [])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onUpload(e.target.files)
      e.target.value = ''
    }
  }, [onUpload])

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        disabled
          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
          : dragActive
            ? 'border-blue-500 bg-blue-50 cursor-pointer'
            : 'border-gray-300 hover:border-blue-400 cursor-pointer'
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={!disabled ? handleClick : undefined}
      role="button"
      tabIndex={0}
      onKeyDown={!disabled ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick() } : undefined}
      aria-label="PDF-Dateien hochladen"
    >
      <UploadCloud className={`w-12 h-12 mx-auto mb-3 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} />
      <p className="text-sm font-medium text-gray-700 mb-1">
        {disabled ? 'Upload läuft...' : 'PDF hierher ziehen'}
      </p>
      <p className="text-xs text-gray-500 mb-2">
        oder klicken zum Auswählen
      </p>
      <div className="flex items-center justify-center gap-1 text-xs text-gray-400">
        <FileText className="w-3 h-3" />
        <span>Nur PDF-Dateien</span>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
    </div>
  )
}

interface UploadProgressListProps {
  files: Array<{ name: string; progress?: number; error?: string }>
}

export function UploadProgressList({ files }: UploadProgressListProps) {
  if (files.length === 0) return null

  return (
    <div className="space-y-2 mt-4">
      {files.map((file, i) => (
        <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
          <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-gray-700 truncate">{file.name}</p>
            {file.error ? (
              <div className="flex items-center gap-1 text-red-500">
                <AlertCircle className="w-3 h-3" />
                <span className="text-xs">{file.error}</span>
              </div>
            ) : file.progress != null && file.progress < 100 ? (
              <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                <div
                  className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                  style={{ width: `${file.progress}%` }}
                />
              </div>
            ) : file.progress === 100 ? (
              <p className="text-xs text-green-500">Upload abgeschlossen</p>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  )
}
